<template>
  <button
    v-bind="attrs"
    :id="tabId"
    ref="buttonRef"
    type="button"
    role="tab"
    class="tab"
    :class="{ active: isActive }"
    :aria-selected="isActive.toString()"
    :aria-controls="panelId"
    :tabindex="isFocused ? 0 : -1"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <slot></slot>
  </button>
</template>

<script setup>
import { computed, defineOptions, inject, ref, useAttrs, watch } from 'vue'
import uniqueId from 'lodash/uniqueId'
import { tabKey } from './key'

defineOptions({ inheritAttrs: false })
const { id, ...attrs } = useAttrs()

const emit = defineEmits(['activate', 'register'])

const tabsState = inject(tabKey)
const buttonRef = ref(null)

const tabId = id ?? uniqueId('tab-')
const tabIndex = tabsState.registerTab(tabId) // Index of the tab in the tablist, not tabindex for focus
emit('register', tabIndex)

const isActive = computed(() => tabsState.activeIndex.value === tabIndex)
const isFocused = computed(() => tabsState.focusedIndex.value === tabIndex)
const panelId = computed(() => {
  const panelId = tabsState.panels.value[tabIndex]
  return panelId
})

watch(tabsState.focusedIndex, (focusedIndex) => {
  if (focusedIndex === tabIndex) {
    buttonRef.value?.focus()
  }
})

const handleClick = () => {
  tabsState.activateTab(tabIndex)
  emit('activate', tabIndex)
}

const handleKeyDown = (event) => {
  const tabsCount = tabsState.tabs.value.length
  let nextIndex = tabIndex

  switch (event.key) {
    case 'ArrowRight':
      nextIndex = (nextIndex + 1) % tabsCount
      break
    case 'ArrowLeft':
      nextIndex = (nextIndex - 1 + tabsCount) % tabsCount
      break
    case 'Home':
      nextIndex = 0
      break
    case 'End':
      nextIndex = tabsCount - 1
      break
    case 'Enter':
    case ' ':
      tabsState.activateTab(tabIndex)
      emit('activate', tabIndex)
      return
    case 'Tab':
      nextIndex = tabsState.activeIndex.value
      tabsState.focusTab(nextIndex)
      return
    default:
      return
  }

  event.preventDefault()
  event.stopPropagation()
  tabsState.focusTab(nextIndex)
}
</script>

<style scoped>
.tab {
  @apply text-neutral-900
    outline-none
    mb-2
    flex
    items-center
    justify-center
    rounded-lg
    bg-transparent
    pb-2
    pl-4
    pr-4
    pt-2
    text-sm
    font-bold
    leading-6
    transition-colors
    duration-500
    ease-in-out;

  --tw-ring-inset: inset;

  &:after {
    content: ' ';
    transition-property: transform, opacity;
    @apply absolute
      -bottom-2
      h-1
      w-full
      translate-y-1
      transform
      rounded-full
      opacity-0
      duration-500
      ease-in-out;
  }

  &.active {
    @apply text-accent relative;

    &::after {
      @apply bg-accent translate-y-0 opacity-100;
    }
  }

  &:not(.active):hover {
    @apply bg-primary-500 bg-opacity-5;
  }
}
</style>
